'use client';
import { useState } from 'react';
import Link from 'next/link';

const FAQS = [
    {
        category: 'General',
        items: [
            { q: 'What is Obvio?', a: 'Obvio is a live multiplayer elimination game where players compete to be the last one standing. The twist? You have to give an answer that is valid but NOT the most popular one.' },
            { q: 'Is Obvio free to play?', a: 'Yes! We run free-to-play games every hour. We also have high-stakes cash games for players looking for more intense competition.' },
            { q: 'In which countries is Obvio available?', a: 'Obvio is currently available for real-money play in England, Scotland, and Wales. Free games are available globally.' },
        ]
    },
    {
        category: 'Gameplay',
        items: [
            { q: 'How is the "Consensus Answer" determined?', a: 'The consensus (or "System Answer") is the most commonly submitted correct answer from all live players in that specific round.' },
            { q: 'What happens if there is a tie?', a: 'If multiple answers have the same highest frequency, all players who gave any of those tied answers are eliminated.' },
            { q: 'Can I use Google during a game?', a: 'You can, but each round has a 15-second time limit, so you have to be fast!' },
        ]
    },
    {
        category: 'Payments & Prizes',
        items: [
            { q: 'How do I get paid?', a: 'Winnings are credited to your in-game wallet instantly. You can withdraw to your bank account or crypto wallet once you reach the £5 minimum.' },
            { q: 'How long do withdrawals take?', a: 'We process all withdrawal requests within 5 working days.' },
            { q: 'Is there a fee for playing?', a: 'Free games have no entry fee. Paid games have a clear entry fee, and we take a small platform fee (rake) from the total prize pool.' },
        ]
    }
];

function FAQItem({ q, a }: { q: string, a: string }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-gray-100 last:border-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full py-6 flex items-center justify-between text-left hover:text-indigo-600 transition-colors group"
            >
                <span className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors pr-8">{q}</span>
                <span className={`text-2xl transition-transform duration-300 ${isOpen ? 'rotate-45 text-indigo-500' : 'text-gray-300'}`}>+</span>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">{a}</p>
            </div>
        </div>
    );
}

export default function FAQPage() {
    return (
        <div className="min-h-screen bg-[#F8F7F4]">
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F8F7F4]/90 border-b border-gray-200/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-black text-lg">O</span>
                        </div>
                        <span className="font-extrabold text-xl tracking-tight text-gray-900">OBVIO</span>
                    </Link>
                    <Link href="/" className="bg-gray-900 text-white px-4 py-2 rounded-full font-semibold hover:bg-gray-800 transition-colors text-sm">
                        ← Home
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-16">
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">FAQ</h1>
                    <p className="text-gray-500 text-lg">Everything you need to know about Obvio.</p>
                </div>

                <div className="space-y-12">
                    {FAQS.map((cat, idx) => (
                        <div key={idx} className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                            <h2 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-6">{cat.category}</h2>
                            <div className="divide-y divide-gray-50">
                                {cat.items.map((item, iIdx) => (
                                    <FAQItem key={iIdx} q={item.q} a={item.a} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <p className="text-gray-500 mb-4">Can’t find what you’re looking for?</p>
                    <Link href="/contact" className="text-indigo-600 font-bold hover:underline">
                        Send us a message →
                    </Link>
                </div>
            </main>

            <footer className="py-12 border-t border-gray-200 text-center">
                <p className="text-gray-400 text-sm">© 2026 OBVIO. All rights reserved.</p>
            </footer>
        </div>
    );
}
